import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  REQUEST_TYPES,
  useCreateRequest,
  type RequestType,
} from '@/hooks/useMyRequests';

export function RequestForm() {
  const { t } = useTranslation();
  const create = useCreateRequest();
  const [type, setType] = useState<RequestType>('leave');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const isLeaveOrTravel = type === 'leave' || type === 'travel';
  const showAmount = type === 'expense';

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error(t('requests.error_title_required'));
      return;
    }
    const extra = isLeaveOrTravel
      ? { from, to }
      : showAmount
        ? { amount: from }
        : null;
    create.mutate(
      {
        type,
        title: title.trim(),
        description: description.trim(),
        ...(extra ? { extra } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t('requests.toast_submitted'));
          setTitle('');
          setDescription('');
          setFrom('');
          setTo('');
        },
        onError: () => toast.error(t('requests.toast_error')),
      },
    );
  }

  return (
    <form className="req-form" onSubmit={onSubmit}>
      <div>
        <div className="req-section-label">{t('requests.choose_type')}</div>
        <div className="req-type-grid">
          {REQUEST_TYPES.map((rt) => (
            <button
              key={rt.key}
              type="button"
              className={type === rt.key ? 'req-type-btn active' : 'req-type-btn'}
              onClick={() => setType(rt.key)}
            >
              <div className="req-type-icon">
                <span className="material-symbols-outlined">{rt.icon}</span>
              </div>
              <span className="req-type-label">{t(rt.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="req-field">
        <label className="req-field-label" htmlFor="req-title">
          {t('requests.field_title')}
        </label>
        <input
          id="req-title"
          className="req-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('requests.field_title_placeholder')}
        />
      </div>

      {isLeaveOrTravel && (
        <div className="req-field-row">
          <div className="req-field">
            <label className="req-field-label" htmlFor="req-from">
              {t('requests.field_from')}
            </label>
            <input
              id="req-from"
              className="req-input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="req-field">
            <label className="req-field-label" htmlFor="req-to">
              {t('requests.field_to')}
            </label>
            <input
              id="req-to"
              className="req-input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      )}

      {showAmount && (
        <div className="req-field">
          <label className="req-field-label" htmlFor="req-amount">
            {t('requests.field_amount')}
          </label>
          <input
            id="req-amount"
            className="req-input"
            type="number"
            inputMode="decimal"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="0.00"
          />
        </div>
      )}

      <div className="req-field">
        <label className="req-field-label" htmlFor="req-desc">
          {t('requests.field_description')}
        </label>
        <textarea
          id="req-desc"
          className="req-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('requests.field_description_placeholder')}
        />
      </div>

      <button type="submit" className="req-submit" disabled={create.isPending}>
        <span className="material-symbols-outlined">send</span>
        {create.isPending ? t('requests.submitting') : t('requests.submit')}
      </button>
    </form>
  );
}
